import React, { useState, useEffect } from "react";
import { FaTimes, FaMoneyBillWave, FaCreditCard, FaHistory, FaShoppingBag, FaArrowLeft, FaCheck, FaCoins, FaHandHoldingUsd, FaPercent, FaWallet } from "react-icons/fa";
import InputMask from "react-input-mask";
import { formatSumma } from "../utils/format";
import "./PaymentModal.css";
import clickLogo from "../Rasm/click_logo.png";
import paymeLogo from "../Rasm/payme_logo.png";
import paynetLogo from "../Rasm/paynet_logo.png";
import uzumLogo from "../Rasm/uzum_logo.png";
import projectLogo from "../Rasm/logo.png";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CONFIG from "../config";

// Mapping Constants
const PAYMENT_METHODS = {
    1: 'Click',
    2: 'Payme',
    3: 'Paynet',
    4: 'Uzum Bank',
    5: 'Karta'
};

export default function PaymentModal({ isOpen, onClose, userData }) {
    const [activeTab, setActiveTab] = useState("pay"); // 'pay', 'history', 'purchases'
    const [selectedMethod, setSelectedMethod] = useState(null); // 'click', 'payme', 'card', etc.
    const [cardData, setCardData] = useState({ number: "", expiry: "", amount: "" });
    const [historyData, setHistoryData] = useState([]);
    const [purchasesData, setPurchasesData] = useState([]);
    const [tangaData, setTangaData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // Ref to track which tabs have been fetched to prevent duplicates in StrictMode
    const loadedRef = React.useRef({ history: false, tanga: false, purchases: false });

    // Initial load when open
    useEffect(() => {
        if (isOpen) {
            setIsClosing(false); // Reset closing state when opened
        } else {
            // Optional: Reset loaded state when closed if we want to re-fetch next time
            // loadedRef.current = { history: false, tanga: false, purchases: false };
            // But for now, let's keep it cached per session
        }
    }, [isOpen]);

    // Fetch Data on Tab Change
    useEffect(() => {
        if (isOpen) {
            if (activeTab === 'history' && !loadedRef.current.history) {
                loadedRef.current.history = true; // Mark immediately
                fetchHistory();
            } else if (activeTab === 'tanga' && !loadedRef.current.tanga) {
                loadedRef.current.tanga = true;
                fetchTanga();
            } else if (activeTab === 'purchases' && !loadedRef.current.purchases) {
                loadedRef.current.purchases = true;
                fetchPurchases();
            }
        }
    }, [activeTab, isOpen]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('loyiha_token');
            const uid = userData?.id || 0;
            const response = await fetch(`${CONFIG.API_BASE_URL}/get_tolovlar_tarixi.php?token=${token}&user_id=${uid}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.status === 'success') {
                setHistoryData(data.data);
            }
        } catch (error) {
            console.error("Error fetching history:", error);
            // toast.error("Tarixni yuklashda xatolik");
        } finally {
            setLoading(false);
        }
    };

    const fetchTanga = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('loyiha_token');
            const uid = userData?.id || 0;
            const response = await fetch(`${CONFIG.API_BASE_URL}/get_tanga_tarixi.php?token=${token}&user_id=${uid}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.status === 'success') {
                setTangaData(data.data);
            }
        } catch (error) {
            console.error("Error fetching tanga history:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchases = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('loyiha_token');
            const uid = userData?.id || 0;
            const response = await fetch(`${CONFIG.API_BASE_URL}/get_xaridlar_tarixi.php?token=${token}&user_id=${uid}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.status === 'success') {
                setPurchasesData(data.data);
            }
        } catch (error) {
            console.error("Error fetching purchases:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateTotals = () => {
        const historyTotal = historyData.reduce((acc, current) => acc + (parseFloat(current.summa) || 0), 0);
        const tangaTotal = tangaData.reduce((acc, current) => acc + (parseFloat(current.narx) || 0), 0);
        const purchasesTotal = purchasesData.reduce((acc, current) => acc + (parseFloat(current.summa) || 0), 0);

        const tangaCoinTotal = tangaData.reduce((acc, current) => acc + (parseFloat(current.tanga) || 0), 0);
        const purchasesCoinTotal = purchasesData.reduce((acc, current) => acc + (parseFloat(current.tanga) || 0), 0);

        return {
            historyTotal: Math.round(historyTotal),
            tangaTotal: Math.round(tangaTotal),
            purchasesTotal: Math.round(purchasesTotal),
            tangaCoinTotal: Math.round(tangaCoinTotal * 10) / 10,
            purchasesCoinTotal: Math.round(purchasesCoinTotal * 10) / 10
        };
    };

    const { historyTotal, tangaTotal, purchasesTotal, tangaCoinTotal, purchasesCoinTotal } = calculateTotals();

    const handleClose = () => {
        setSelectedMethod(null);
        setCardData({ number: "", expiry: "", amount: "" });
        onClose();
    };

    const handleMethodSelect = (method) => {
        if (method === 'card') {
            setSelectedMethod('card');
        } else {
            toast.info(`${method} tanlandi (Integratsiya qilinmoqda)`);
        }
    };

    const handleBack = () => {
        setSelectedMethod(null);
    };

    const handlePayment = () => {
        // Mock payment logic for now
        if (!cardData.amount || cardData.amount < 1000) {
            toast.warning("Minimal to'lov summasi 1000 so'm");
            return;
        }
        toast.info("To'lov so'rovi yuborildi...");
    };

    if (!isOpen) return null;

    return (
        <div className={`payment-modal-overlay ${isClosing ? 'modal-overlay-fade-out' : 'modal-overlay-fade-in'}`}>
            <div
                className={`payment-modal-content ${isClosing ? 'modal-content-pop-out' : 'modal-content-pop-in'}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="payment-modal-header">
                    <div className="payment-modal-title">
                        {selectedMethod === 'card' ? (
                            <span className="payment-back-btn" onClick={handleBack}>
                                <FaArrowLeft style={{ marginRight: 10 }} /> Plastik Karta
                            </span>
                        ) : (
                            "Hisob va To'lovlar"
                        )}
                    </div>
                    <div className="payment-modal-close" onClick={handleClose}>
                        <FaTimes />
                    </div>
                </div>

                {selectedMethod !== 'card' && (
                    <div className="payment-modal-tabs">
                        <div
                            className={`payment-tab ${activeTab === 'pay' ? 'active' : ''}`}
                            onClick={() => setActiveTab('pay')}
                        >
                            <FaMoneyBillWave style={{ marginRight: 5 }} /> To'lov
                        </div>
                        <div
                            className={`payment-tab ${activeTab === 'history' ? 'active' : ''}`}
                            onClick={() => setActiveTab('history')}
                        >
                            <FaHistory style={{ marginRight: 5 }} /> Tarix
                        </div>
                        <div
                            className={`payment-tab ${activeTab === 'tanga' ? 'active' : ''}`}
                            onClick={() => setActiveTab('tanga')}
                        >
                            <FaCoins style={{ marginRight: 5 }} /> Tanga
                        </div>
                        <div
                            className={`payment-tab ${activeTab === 'purchases' ? 'active' : ''}`}
                            onClick={() => setActiveTab('purchases')}
                        >
                            <FaShoppingBag style={{ marginRight: 5 }} /> Xaridlar
                        </div>
                    </div>
                )}

                <div className="payment-modal-body">
                    {selectedMethod === 'card' ? (
                        <div className="card-payment-form">
                            <div className="card-visual">
                                <div className="card-top-row">
                                    <div className="card-chip"></div>
                                    <div className="card-logo">
                                        <img src={projectLogo} alt="Logo" style={{ height: 40, filter: 'brightness(0) invert(1)' }} />
                                    </div>
                                </div>
                                <div className="card-input-group number-group">
                                    <label>Karta Raqami</label>
                                    <InputMask
                                        mask="9999 9999 9999 9999"
                                        value={cardData.number}
                                        onChange={(e) => setCardData({ ...cardData, number: e.target.value })}
                                        placeholder="0000 0000 0000 0000"
                                        className="card-input-transparent"
                                    />
                                </div>
                                <div className="card-input-group date-group">
                                    <label>Amal qilish muddati</label>
                                    <InputMask
                                        mask="99/99"
                                        value={cardData.expiry}
                                        onChange={(e) => setCardData({ ...cardData, expiry: e.target.value })}
                                        placeholder="OO/YY"
                                        className="card-input-transparent"
                                    />
                                </div>
                            </div>

                            <div className="amount-input-section">
                                <label>To'lov summasi (so'm)</label>
                                <input
                                    type="number"
                                    placeholder="Masalan: 50000"
                                    value={cardData.amount}
                                    onChange={(e) => setCardData({ ...cardData, amount: e.target.value })}
                                    className="amount-input"
                                />
                            </div>

                            <div className="card-actions">
                                <button className="btn-cancel" onClick={handleBack}>
                                    Bekor qilish
                                </button>
                                <button className="btn-pay" onClick={handlePayment}>
                                    To'lov qilish <FaCheck style={{ marginLeft: 8 }} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'pay' && (
                                <div className="payment-section fade-in">
                                    <p style={{ marginBottom: 15, textAlign: 'center' }}>To'lov tizimini tanlang:</p>
                                    <div className="payment-methods-grid">
                                        <div className="payment-method-card" onClick={() => handleMethodSelect('click')}>
                                            <img src={clickLogo} alt="Click" className="payment-method-img" />
                                            <span className="payment-tooltip">Click orqali to'lov</span>
                                        </div>
                                        <div className="payment-method-card" onClick={() => handleMethodSelect('payme')}>
                                            <img src={paymeLogo} alt="Payme" className="payment-method-img" />
                                            <span className="payment-tooltip">Payme orqali to'lov</span>
                                        </div>
                                        <div className="payment-method-card" onClick={() => handleMethodSelect('paynet')}>
                                            <img src={paynetLogo} alt="Paynet" className="payment-method-img" />
                                            <span className="payment-tooltip">Paynet orqali to'lov</span>
                                        </div>
                                        <div className="payment-method-card" onClick={() => handleMethodSelect('uzum')}>
                                            <img src={uzumLogo} alt="Uzum" className="payment-method-img uzum-img" />
                                            <span className="payment-tooltip">Uzum Bank orqali to'lov</span>
                                        </div>
                                        <div className="payment-method-card" onClick={() => handleMethodSelect('card')}>
                                            <FaCreditCard className="payment-method-icon" />
                                            <span className="payment-tooltip">Plastik karta orqali</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'history' && (
                                <div className="history-section fade-in">
                                    {loading ? <p className="text-center">Yuklanmoqda...</p> : (
                                        <>
                                            <table className="history-table">
                                                <thead>
                                                    <tr>
                                                        <th>Sana</th>
                                                        <th>Usul</th>
                                                        <th>Summa</th>
                                                        <th>Holat</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historyData.length > 0 ? (
                                                        historyData.map((item, index) => {
                                                            const statusKey = item.status == 1 ? 1 : 0;
                                                            const statusText = statusKey === 1 ? 'Muvaffaqiyatli' : 'Rad etilgan';
                                                            const statusClass = statusKey === 1 ? 'status-success' : 'status-failed';
                                                            const methodText = PAYMENT_METHODS[item.usul] || 'Noma\'lum';

                                                            return (
                                                                <tr key={item.id || index}>
                                                                    <td>{item.sana}</td>
                                                                    <td>{methodText}</td>
                                                                    <td>{formatSumma(item.summa)}</td>
                                                                    <td>
                                                                        <span className={`status-badge ${statusClass}`}>
                                                                            {statusText}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr><td colSpan="4" style={{ textAlign: 'center' }}>To'lovlar tarixi mavjud emas</td></tr>
                                                    )}
                                                </tbody>
                                            </table>


                                        </>
                                    )}
                                </div>
                            )}

                            {activeTab === 'tanga' && (
                                <div className="history-section fade-in">
                                    {loading ? <p className="text-center">Yuklanmoqda...</p> : (
                                        <>
                                            <table className="history-table">
                                                <thead>
                                                    <tr>
                                                        <th>Sana</th>
                                                        <th>Masala nomi</th>
                                                        <th>Tanga</th>
                                                        <th>Summa</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tangaData.length > 0 ? (
                                                        tangaData.map((item, index) => (
                                                            <tr key={item.id || index}>
                                                                <td>{item.sana}</td>
                                                                <td title={item.nomi}>
                                                                    <div className="text-truncate" style={{ maxWidth: '180px' }}>
                                                                        {item.nomi}
                                                                    </div>
                                                                </td>
                                                                <td style={{ fontWeight: 'bold', color: '#f1c40f' }}>{item.tanga}</td>
                                                                <td>{formatSumma(item.narx)}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr><td colSpan="4" style={{ textAlign: 'center' }}>Tanga tarixi mavjud emas</td></tr>
                                                    )}
                                                </tbody>
                                            </table>


                                        </>
                                    )}
                                </div>
                            )}

                            {activeTab === 'purchases' && (
                                <div className="purchases-section fade-in">
                                    {loading ? <p className="text-center">Yuklanmoqda...</p> : (
                                        <>
                                            <table className="history-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '100px' }}>Sana</th>
                                                        <th style={{ width: '100px' }}>Turi</th>
                                                        <th style={{ width: '180px' }}>Nomi</th>
                                                        <th>Tanga</th>
                                                        <th>Summa</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {purchasesData.length > 0 ? (
                                                        purchasesData.map((item, index) => (
                                                            <tr key={item.id || index}>
                                                                <td style={{ fontSize: '12px' }}>{item.sana}</td>
                                                                <td style={{ fontWeight: '500' }}>
                                                                    {item.turi === 'Kurs' ? (
                                                                        <><FaShoppingBag style={{ color: '#3498db', fontSize: '0.8rem', marginRight: '4px' }} />Kurs</>
                                                                    ) : (
                                                                        <><FaCoins style={{ color: '#f1c40f', fontSize: '0.8rem', marginRight: '4px' }} />Masala</>
                                                                    )}
                                                                </td>
                                                                <td title={item.nomi}>
                                                                    <div className="text-truncate" style={{ maxWidth: '180px' }}>
                                                                        {item.nomi}
                                                                    </div>
                                                                </td>
                                                                <td style={{ fontWeight: 'bold' }}>
                                                                    {item.original_tanga > item.tanga ? (
                                                                        <div className="tooltip-container side">
                                                                            <span style={{ cursor: 'help' }}>{item.tanga}</span>
                                                                            <span className="payment-tooltip">
                                                                                <FaCoins style={{ marginRight: '5px', color: '#f1c40f' }} />
                                                                                Asl narxi: {formatSumma(item.original_tanga)} tanga<br />
                                                                                {item.discount_percent > 0 ? (
                                                                                    <>
                                                                                        <FaPercent style={{ marginRight: '5px', color: '#2ecc71' }} />
                                                                                        Chegirma: {item.discount_percent}%
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FaCoins style={{ marginRight: '5px', color: '#2ecc71' }} />
                                                                                        Chegirma: {formatSumma(item.original_tanga - item.tanga)} tanga
                                                                                    </>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    ) : item.tanga}
                                                                </td>
                                                                <td>
                                                                    {item.original_tanga > item.tanga ? (
                                                                        <div className="tooltip-container side">
                                                                            <span style={{ cursor: 'help' }}>{formatSumma(item.summa)}</span>
                                                                            <span className="payment-tooltip">
                                                                                <FaHandHoldingUsd style={{ marginRight: '5px', color: '#3498db' }} />
                                                                                Asl narxi: {formatSumma(item.original_tanga * 1000)} so'm<br />
                                                                                {item.discount_percent > 0 ? (
                                                                                    <>
                                                                                        <FaPercent style={{ marginRight: '5px', color: '#2ecc71' }} />
                                                                                        Chegirma: {item.discount_percent}%
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FaCoins style={{ marginRight: '5px', color: '#2ecc71' }} />
                                                                                        Chegirma: {formatSumma((item.original_tanga - item.tanga) * 1000)} so'm
                                                                                    </>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    ) : formatSumma(item.summa)}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr><td colSpan="5" style={{ textAlign: 'center' }}>Xaridlar tarixi mavjud emas</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {selectedMethod !== 'card' && (
                    <div className="payment-modal-footer">
                        {activeTab === 'pay' && (
                            <div className="table-summary-button balance-total">
                                <FaWallet style={{ marginRight: 8 }} />
                                Joriy hisob: <span>{formatSumma(userData?.tanga_balansi || 0)}</span>
                            </div>
                        )}
                        {activeTab === 'history' && historyData.length > 0 && (
                            <div className="table-summary-button history-total">
                                <FaHistory style={{ marginRight: 8 }} />
                                Jami to'lovlar: <span>{formatSumma(historyTotal)}</span>
                            </div>
                        )}
                        {activeTab === 'tanga' && tangaData.length > 0 && (
                            <div className="table-summary-button tanga-total">
                                <FaCoins style={{ marginRight: 8 }} />
                                Jami daromad: <span>{tangaCoinTotal} tanga ({formatSumma(tangaTotal)})</span>
                            </div>
                        )}
                        {activeTab === 'purchases' && purchasesData.length > 0 && (
                            <div className="table-summary-button purchases-total">
                                <FaShoppingBag style={{ marginRight: 8 }} />
                                Jami xaridlar: <span>{purchasesCoinTotal} tanga ({formatSumma(purchasesTotal)})</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
